import { useState, useEffect, useRef } from "react";
import { AiOutlineDownload, AiOutlineDelete } from "react-icons/ai";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import { FiShare2, FiEye } from "react-icons/fi";
import "./style/MasonryGallery.css";

const MasonryGallery = ({
  images,
  onImageClick,
  onDownload,
  onSelect,
  showInteractions = false,
  showShare = false,
  onToggleLike,
  onShare,
  zenMode = false,
  columns = null,
}) => {

  const [activeIndex, setActiveIndex] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [downloadingIndex, setDownloadingIndex] = useState(null);
  // Scope layout to this instance — several galleries can be on the same page
  const galleryRef = useRef(null);

  // Fix mobile detection
  useEffect(() => {
    const checkMobile = () => {
      const touch =
        "ontouchstart" in window || navigator.maxTouchPoints > 0;
      const smallScreen = window.innerWidth <= 900; // breakpoint for mobile
      setIsMobile(touch && smallScreen);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);


      useEffect(() => {
      const resizeAllGridItems = () => {
        const gallery = galleryRef.current;
        if (!gallery) return;
        const items = gallery.querySelectorAll(".masonry-item");

        items.forEach((item) => {
          const img = item.querySelector("img");
          if (img.complete) resizeGridItem(item);
          else {
            img.addEventListener("load", () => resizeGridItem(item));
          }
        });
      };

      const resizeGridItem = (item) => {
        const gallery = item.parentElement;
        const computedStyle = window.getComputedStyle(gallery);
        // Single-column layout: grid-auto-rows is auto, no span needed
        if (computedStyle.gridTemplateColumns.split(" ").length === 1) {
          item.style.gridRowEnd = "auto";
          return;
        }
        const rowHeight = 10; // same as grid-auto-rows
        const rowGap = parseInt(computedStyle.rowGap) || 10;
        const contentHeight = item.querySelector("img").offsetHeight;
        const span = Math.ceil((contentHeight + rowGap) / (rowHeight + rowGap));
        item.style.gridRowEnd = "span " + span;
      };

      resizeAllGridItems();
      window.addEventListener("resize", resizeAllGridItems);

      return () => window.removeEventListener("resize", resizeAllGridItems);
    }, [images]);


  const isSelectionMode = images.some(img => img.isSelected);

  const gridStyle = columns
    ? { gridTemplateColumns: `repeat(${columns}, 1fr)`, gridAutoRows: columns === 1 ? "auto" : "10px" }
    : {};

  return (
    <div ref={galleryRef} className="masonry-gallery" style={gridStyle}>
      {images.map((img, index) => {
          const isActive = activeIndex === index;
        const showButtons = !zenMode && isActive;
        const showPreviewBtn = !zenMode && isSelectionMode;

        return (
          <div
            key={index}
            className={`masonry-item ${img.isSelected ? "selected" : ""} ${img.userLiked ? "user-liked" : ""}`}
            onMouseEnter={() => { if (!isMobile) setActiveIndex(index); }}
            onMouseLeave={() => { if (!isMobile) setActiveIndex(null); }}
            onClick={() => {
              if (isSelectionMode) {
                onSelect && onSelect(index);
                return;
              }
              if (isMobile) {
                if (activeIndex !== index) {
                  setActiveIndex(index);
                  return;
                }
                setActiveIndex(null);
              }
              onImageClick && onImageClick(index);
            }}
          >
            {/* img-wrapper: position:relative scoped to actual image size */}
            <div className="img-wrapper">
              <img
                src={img.src}
                alt={img.alt || `image-${index}`}
                loading="lazy"
                className="masonry-img"
              />

              {/* Select Button — visible on focus or when already selected */}
              {!zenMode && onSelect && (isActive || img.isSelected) && (
                <button
                  className="select-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(index);
                  }}
                >
                  <div className={`select-circle ${img.isSelected ? "selected" : ""}`}></div>
                </button>
              )}

              {/* Download / Preview Button */}
              {showPreviewBtn ? (
                <button
                  className="download-btn"
                  style={{ opacity: 1, visibility: "visible" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onImageClick && onImageClick(index);
                  }}
                >
                  <FiEye className="download-icon" />
                </button>
              ) : showButtons && onDownload ? (
                <button
                  className="download-btn"
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!onDownload || downloadingIndex === index) return;
                    setDownloadingIndex(index);
                    try {
                      await onDownload(index);
                    } finally {
                      setDownloadingIndex(null);
                    }
                  }}
                >
                  {downloadingIndex === index
                    ? <span className="download-spinner" />
                    : <AiOutlineDownload className="download-icon" />}
                </button>
              ) : null}

              {/* Interaction bar: likes + share — visible on focus only */}
              {(showInteractions || (showShare && !!onShare)) && !zenMode && isActive && (
                <div className="interaction-bar">
                  {showInteractions && (
                    <button
                      className="interaction-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleLike && onToggleLike(index);
                      }}
                    >
                      {img.userLiked
                        ? <FaHeart className="like-icon liked" />
                        : <FaRegHeart className="like-icon" />}
                      {img.likeCount > 0 && <span>{img.likeCount}</span>}
                    </button>
                  )}

                  {onShare && (
                    <button
                      className="interaction-btn"
                      title="Share photo"
                      onClick={(e) => {
                        e.stopPropagation();
                        onShare(index);
                      }}
                    >
                      <FiShare2 className="share-icon" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MasonryGallery;
